export const METERS_TO_FEET = 3.28084;
export const METERS_TO_YARDS = METERS_TO_FEET / 3;
export const METERS_TO_MILES = METERS_TO_FEET / 5280;
export const FEET_PER_MILE = 5280;
export const SQMETERS_TO_SQFEET = 10.7639;
export const SQMETERS_TO_ACRES = 0.000247105;
export const SQMETERS_TO_HECTARES = 0.0001;
export const SQMETERS_TO_SQYARDS = METERS_TO_YARDS * METERS_TO_YARDS;
export const SQMETERS_TO_SQKM = 0.000001;
export const SQMETERS_TO_SQMILES = METERS_TO_MILES * METERS_TO_MILES;
export const DISTANCE_UNIT_LABELS = {
    m: 'm',
    km: 'km',
    ft: 'ft',
    yd: 'yd',
    mi: 'mi',
};
export const AREA_UNIT_LABELS = {
    sqm: 'm²',
    sqft: 'ft²',
    acres: 'acres',
    hectares: 'ha',
    sqyd: 'yd²',
    sqkm: 'km²',
    sqmi: 'mi²'
};
export const metersToFeet = (meters) => meters * METERS_TO_FEET;
export const feetToMeters = (feet) => feet / METERS_TO_FEET;
// Slope angle (degrees) to percent grade: rise/run × 100 = tan(θ) × 100.
// 0° → 0%, 45° → 100%. Matches the backend's toPercent (math.Tan) so the
// frontend can surface grade without a reprocess.
export const slopeDegreesToPercentGrade = (degrees) => Math.tan((degrees * Math.PI) / 180) * 100;
export const formatDistance = (meters, unit) => {
    switch (unit) {
        case 'km': return `${(meters / 1000).toFixed(3)} km`;
        case 'ft': return `${(meters * METERS_TO_FEET).toFixed(1)} ft`;
        case 'yd': return `${(meters * METERS_TO_YARDS).toFixed(1)} yd`;
        case 'mi': return `${(meters * METERS_TO_MILES).toFixed(3)} mi`;
        case 'm':
        default: return `${meters.toFixed(1)} m`;
    }
};
// formatDistance with the same explicit-unit semantics, kept as a separate symbol
// for callers that want a guaranteed-no-magnitude-promotion function (now identical
// to formatDistance, which also doesn't promote since the user picks the unit explicitly).
export const formatDistancePrecise = formatDistance;
export const formatElevation = (meters, unit) => {
    // Elevation is most natural in m or ft; for other distance units fall back sensibly.
    if (unit === 'ft' || unit === 'yd' || unit === 'mi') {
        return `${(meters * METERS_TO_FEET).toFixed(1)} ft`;
    }
    return `${meters.toFixed(1)} m`;
};
// One decimal place with a trailing ".0" dropped: 6.5617 -> "6.6", 2.0 -> "2".
export const formatOneDecimal = (value) => {
    const s = value.toFixed(1);
    return s.endsWith('.0') ? s.slice(0, -2) : s;
};
// Compact whole-unit elevation label for legend ramps ("512 m" / "1680 ft").
// Distinct from formatElevation, which carries a decimal place — too much
// precision for a ramp label sitting beside a colour bar.
export const formatElevationLabel = (meters, system) => system === 'imperial'
    ? `${Math.round(metersToFeet(meters))} ft`
    : `${Math.round(meters)} m`;
export const formatPerimeter = (meters, unit) => {
    if (unit === 'sqft' || unit === 'acres' || unit === 'sqyd' || unit === 'sqmi') {
        const feet = meters * METERS_TO_FEET;
        if (feet >= FEET_PER_MILE) {
            return `${(feet / FEET_PER_MILE).toFixed(2)} mi`;
        }
        return `${feet.toFixed(0)} ft`;
    }
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${meters.toFixed(0)} m`;
};
export const formatAreaFromSqMeters = (squareMeters, unit) => {
    switch (unit) {
        case 'sqft':
            return `${(squareMeters * SQMETERS_TO_SQFEET).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${AREA_UNIT_LABELS.sqft}`;
        case 'sqyd':
            return `${(squareMeters * SQMETERS_TO_SQYARDS).toLocaleString(undefined, { maximumFractionDigits: 0 })} ${AREA_UNIT_LABELS.sqyd}`;
        case 'acres':
            return `${(squareMeters * SQMETERS_TO_ACRES).toFixed(2)} ${AREA_UNIT_LABELS.acres}`;
        case 'hectares':
            return `${(squareMeters * SQMETERS_TO_HECTARES).toFixed(2)} ${AREA_UNIT_LABELS.hectares}`;
        case 'sqkm':
            return `${(squareMeters * SQMETERS_TO_SQKM).toFixed(4)} ${AREA_UNIT_LABELS.sqkm}`;
        case 'sqmi':
            return `${(squareMeters * SQMETERS_TO_SQMILES).toFixed(4)} ${AREA_UNIT_LABELS.sqmi}`;
        case 'sqm':
        default:
            return `${squareMeters.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${AREA_UNIT_LABELS.sqm}`;
    }
};
// Strict squared-distance formatter: m → m², km → km², ft → ft², yd → yd²,
// mi → mi². No magnitude promotion (e.g. ft² to acres). Used during live
// measure previews where the area unit must visibly match the radius/
// distance unit the user is reading.
export const formatSquaredDistanceArea = (squareMeters, unit) => {
    switch (unit) {
        case 'km': {
            const sqkm = squareMeters / 1000000;
            return `${sqkm.toFixed(4)} km²`;
        }
        case 'ft': {
            const sqft = squareMeters * METERS_TO_FEET * METERS_TO_FEET;
            return `${sqft.toLocaleString(undefined, { maximumFractionDigits: 0 })} ft²`;
        }
        case 'yd': {
            const sqyd = squareMeters * METERS_TO_YARDS * METERS_TO_YARDS;
            return `${sqyd.toLocaleString(undefined, { maximumFractionDigits: 0 })} yd²`;
        }
        case 'mi': {
            const sqmi = squareMeters * METERS_TO_MILES * METERS_TO_MILES;
            return `${sqmi.toFixed(4)} mi²`;
        }
        case 'm':
        default:
            return `${squareMeters.toLocaleString(undefined, { maximumFractionDigits: 0 })} m²`;
    }
};
export const formatAreaFromDistanceUnit = (squareMeters, unit) => {
    if (unit === 'ft') {
        const squareFeet = squareMeters * METERS_TO_FEET * METERS_TO_FEET;
        if (squareFeet >= 43560) {
            return `${(squareFeet / 43560).toFixed(2)} acres`;
        }
        return `${squareFeet.toFixed(1)} sq ft`;
    }
    if (squareMeters >= 1000000) {
        return `${(squareMeters / 1000000).toFixed(2)} sq km`;
    }
    if (squareMeters >= 10000) {
        return `${(squareMeters / 10000).toFixed(2)} ha`;
    }
    return `${squareMeters.toFixed(1)} sq m`;
};
export const convertAreaToUnit = (squareMeters, unit) => {
    switch (unit) {
        case 'sqft':
            return squareMeters * SQMETERS_TO_SQFEET;
        case 'sqyd':
            return squareMeters * SQMETERS_TO_SQYARDS;
        case 'acres':
            return squareMeters * SQMETERS_TO_ACRES;
        case 'hectares':
            return squareMeters * SQMETERS_TO_HECTARES;
        case 'sqkm':
            return squareMeters * SQMETERS_TO_SQKM;
        case 'sqmi':
            return squareMeters * SQMETERS_TO_SQMILES;
        case 'sqm':
        default:
            return squareMeters;
    }
};
export const convertElevationBreakdownItems = (items, isImperial) => {
    if (!isImperial)
        return items;
    return items.map(item => ({
        ...item,
        title: item.title.replace(/([\d.]+)\s*-\s*([\d.]+)m/, (_, min, max) => {
            const minFt = (parseFloat(min) * METERS_TO_FEET).toFixed(0);
            const maxFt = (parseFloat(max) * METERS_TO_FEET).toFixed(0);
            return `${minFt} - ${maxFt}ft`;
        })
    }));
};
